import { HttpErrorType } from '../http/app-http-error';

/** Supported UI languages (BCP 47 language subtags). */
export type Lang = 'pl' | 'en';

/** Languages available in the account deletion panel. */
export const AVAILABLE_LANGS: Lang[] = ['pl', 'en'];

/**
 * Localized copy keys for the account deletion screen.
 * Add a new key here and in every language object in {@link TRANSLATIONS}.
 */
export interface TranslationStrings {
  /** Accessible page title (sr-only heading). */
  pageTitle: string;
  /** Main message when deletion is scheduled. */
  deletionIntro: string;
  /** Text before the formatted deletion date. */
  deletionSchedulePrefix: string;
  /** Text before the formatted deletion time. */
  deletionScheduleSuffix: string;
  /** Call-to-action under the scheduled deletion date. */
  deletionSubtitle: string;
  /** Primary button — cancel scheduled deletion. */
  regainButton: string;
  /** Destructive button — permanent delete (non-production only). */
  removeButton: string;
  /** Shown when the token is invalid or missing. */
  errorInvalidToken: string;
  /** Shown on HTTP 401. */
  errorUnauthorized: string;
  /** Shown on HTTP 5xx. */
  errorServer: string;
  /** Shown on network/CORS failure (status 0). */
  errorNetwork: string;
  /** Fallback error message. */
  errorUnknown: string;
  /** Success message after regain. */
  regainSuccess: string;
  /** Success message after permanent removal. */
  removeSuccess: string;
  /** Accessible label for the loading spinner. */
  loadingLabel: string;
}

/**
 * Full translation table per language.
 * To add a language: extend {@link Lang}, add an entry here, and update {@link AVAILABLE_LANGS}.
 */
export const TRANSLATIONS: Record<Lang, TranslationStrings> = {
  pl: {
    pageTitle: 'OpenApp',
    deletionIntro:
      'Twoje konto OpenApp jest w procesie automatycznego kasowania.',
    deletionSchedulePrefix: 'Konto zostanie automatycznie skasowane',
    deletionScheduleSuffix: 'o godzinie',
    deletionSubtitle:
      'Jeżeli chcesz odzyskać do niego dostęp, kliknij przycisk poniżej.',
    regainButton: 'Odzyskaj konto',
    removeButton: 'Usuń trwale konto',
    errorInvalidToken: 'Pobrany token nie jest poprawny. Spróbuj ponownie.',
    errorUnauthorized: 'Brak uprawnień do wykonania tej operacji.',
    errorServer: 'Wystąpił problem z serwerem. Spróbuj ponownie później.',
    errorNetwork: 'Brak połączenia z siecią. Sprawdź połączenie i spróbuj ponownie.',
    errorUnknown: 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
    regainSuccess: 'Twoje konto zostało przywrócone.',
    removeSuccess: 'Twoje konto zostało trwale usunięte z systemu.',
    loadingLabel: 'Ładowanie...',
  },
  en: {
    pageTitle: 'OpenApp',
    deletionIntro: 'Your account is in the process of being automatically deleted.',
    deletionSchedulePrefix: 'Your account will be automatically deleted on',
    deletionScheduleSuffix: 'at',
    deletionSubtitle:
      'If you want to regain access to your account, please click below.',
    regainButton: 'Regain my account',
    removeButton: 'Remove permanently',
    errorInvalidToken: 'Incorrect token. Please try again.',
    errorUnauthorized: 'You are not authorized to perform this action.',
    errorServer: 'A server error occurred. Please try again later.',
    errorNetwork: 'Network connection failed. Check your connection and try again.',
    errorUnknown: 'An unexpected error occurred. Please try again.',
    regainSuccess: 'Account has been regained.',
    removeSuccess: 'Account has been permanently deleted',
    loadingLabel: 'Loading...',
  },
};

/**
 * Resolves the user-facing error string for the active language and error type.
 *
 * @param translations - Copy bundle for the current {@link Lang}.
 * @param errorType - Mapped error from {@link AppHttpError}, or `null` for token errors.
 * @returns Localized error message.
 *
 * @example
 * ```ts
 * errorMessageForType(TRANSLATIONS.pl, HttpErrorType.ServerError);
 * // => 'Wystąpił problem z serwerem...'
 * ```
 */
export function errorMessageForType(
  translations: TranslationStrings,
  errorType: HttpErrorType | null,
): string {
  switch (errorType) {
    case HttpErrorType.InvalidToken:
      return translations.errorInvalidToken;
    case HttpErrorType.Unauthorized:
      return translations.errorUnauthorized;
    case HttpErrorType.ServerError:
      return translations.errorServer;
    case HttpErrorType.Network:
      return translations.errorNetwork;
    case HttpErrorType.Unknown:
      return translations.errorUnknown;
    default:
      return translations.errorInvalidToken;
  }
}
